import { cn } from "@beweegbaas/ui";
import { useRouterState } from "@tanstack/react-router";
import { AnimatePresence, motion } from "motion/react";
import { useEffect, useState } from "react";
import { useSpinDelay } from "spin-delay";

/**
 * App-wide loading indicator that reads TanStack Router state.
 * `s.isLoading` is true whenever a loader is in flight, including same-route revalidation.
 * Mounted in the root document to cover every route.
 */
export function GlobalSpinner() {
  const isPending = useRouterState({ select: (s) => s.isLoading });
  const showSpinnerBase = useSpinDelay(Boolean(isPending));
  const [hasPendingElement, setHasPendingElement] = useState(false);

  // Check if there's an element with data-is-pending on the page (a route that renders its own,
  // more specific loading affordance opts the global spinner out).
  useEffect(() => {
    const checkPendingElement = () => {
      const pendingElement = document.querySelector("[data-is-pending]");
      setHasPendingElement(Boolean(pendingElement));
    };

    // Check initially
    checkPendingElement();

    // Set up a MutationObserver to watch for changes
    const observer = new MutationObserver(checkPendingElement);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["data-is-pending"],
    });

    return () => observer.disconnect();
  }, []);

  // Only show spinner if there's no pending element on the page
  const showSpinner = showSpinnerBase && !hasPendingElement;

  return (
    <>
      <AnimatePresence>
        {showSpinner && (
          <motion.div
            key="spinner-background"
            initial={{
              opacity: 0,
            }}
            animate={{
              opacity: 1,
            }}
            exit={{
              opacity: 0,
            }}
            className={cn(
              "pointer-events-none",
              "mask-b-from-50% mask-radial-[50%_90%] mask-radial-from-80%",
              `fixed inset-0 top-0 left-1/2 z-50 h-12
              w-[80%] -translate-x-1/2 backdrop-blur-[0px]`,
            )}
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {showSpinner && (
          <motion.div
            key="spinner"
            initial={{
              opacity: 0,
            }}
            animate={{
              opacity: 1,
            }}
            exit={{
              opacity: 0,
            }}
            className={cn(
              `bg-radial-[at_50%_-60%] from-blue-800 from-[-50%] to-blue-800/0
              to-50% bg-contain`,
              "pointer-events-none",
              "h-40 w-full",
              `fixed top-0 left-1/2 z-50 flex -translate-x-1/2 items-start
              justify-center p-4`,
            )}
          >
            <IconSvgSpinnersRingResize />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
